import React, { useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../apiServices/axios';
import { Dismiss20Regular, Eye16Regular, EyeOff16Regular } from '@fluentui/react-icons';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logoutWithAudit } from '../../redux/usersSlice';
import modalStyles from './css/SSOUnlinkModal.module.css';

const SSOUnlinkModal = ({ isOpen, onClose }) => {
    const [newPassword, setNewPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const canSubmit = newPassword.trim().length >= 8;

    const handleSubmit = async e => {
        e.preventDefault();
        if (!canSubmit || isLoading) return;

        setIsLoading(true);
        try {
            await axiosInstance.post('accounts/settings/unlink-azure/', {
                new_password: newPassword,
            });
            toast.success('Password set successfully');
            setIsSuccess(true);
        } catch (error) {
            let errorMessage = 'Failed to set password';

            if (error.response?.data) {
                const errorData = error.response.data;

                if (typeof errorData === 'object') {
                    const errorKeys = Object.keys(errorData);
                    if (errorKeys.length > 0) {
                        const firstKey = errorKeys[0];
                        const firstError = errorData[firstKey];

                        if (Array.isArray(firstError) && firstError.length > 0) {
                            errorMessage = firstError[0];
                        } else if (typeof firstError === 'string') {
                            errorMessage = firstError;
                        }
                    }
                } else if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                } else if (errorData.detail) {
                    errorMessage = errorData.detail;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoginClick = async () => {
        localStorage.removeItem('persist:root');
        await dispatch(logoutWithAudit());
        navigate('/login');
        handleClose();
    };

    const handleClose = () => {
        setNewPassword('');
        setShowNewPassword(false);
        setIsLoading(false);
        setIsSuccess(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className={modalStyles.overlay} role="presentation" onClick={handleClose}>
            <div
                className={modalStyles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="set-password-title"
                onClick={e => e.stopPropagation()}>
                <div className={modalStyles.header}>
                    <h2 id="set-password-title" className={modalStyles.title}>
                        {isSuccess ? 'Password set' : 'Set Password'}
                    </h2>
                    <button type="button" className={modalStyles.closeButton} onClick={handleClose} aria-label="Close">
                        <Dismiss20Regular />
                    </button>
                </div>

                <div className={modalStyles.body}>
                    {!isSuccess ? (
                        <form onSubmit={handleSubmit} className={modalStyles.form}>
                            <p className={modalStyles.description}>Create a password to use when signing in to Lens.</p>
                            <div className={modalStyles.fieldGroup}>
                                <label className={modalStyles.fieldLabel} htmlFor="sso-unlink-password">
                                    Password
                                </label>
                                <div className={modalStyles.passwordField}>
                                    <input
                                        id="sso-unlink-password"
                                        value={newPassword}
                                        name="password"
                                        type={showNewPassword ? 'text' : 'password'}
                                        onChange={e => setNewPassword(e.target.value)}
                                        className={modalStyles.passwordInput}
                                        placeholder="Create a password"
                                        autoComplete="new-password"
                                        minLength={8}
                                    />
                                    <button
                                        type="button"
                                        className={modalStyles.togglePasswordButton}
                                        onClick={() => setShowNewPassword(prev => !prev)}
                                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}>
                                        {showNewPassword ? <Eye16Regular /> : <EyeOff16Regular />}
                                    </button>
                                </div>
                            </div>
                            <div className={modalStyles.actions}>
                                <button type="submit" className={modalStyles.primaryButton} disabled={!canSubmit || isLoading}>
                                    {isLoading ? (
                                        <>
                                            <span className={modalStyles.spinner} />
                                            Setting…
                                        </>
                                    ) : (
                                        'Set Password'
                                    )}
                                </button>
                                <button type="button" className={modalStyles.cancelButton} onClick={handleClose}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    ) : (
                        <>
                            <p className={modalStyles.description}>
                                Your password has been set successfully. Sign in again to complete two-factor authentication setup.
                            </p>
                            <div className={modalStyles.actions}>
                                <button type="button" className={modalStyles.primaryButton} onClick={handleLoginClick}>
                                    Sign in
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SSOUnlinkModal;
